import React, { createContext, useState, useContext, useEffect } from "react";
import axios from "../api/axios";
import toast from "react-hot-toast";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [token, setToken] = useState(null);

  useEffect(() => {
    const storedToken = localStorage.getItem("access_token");
    if (storedToken) {
      setToken(storedToken);
      axios.defaults.headers.common["Authorization"] = `Bearer ${storedToken}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get("/auth/profile/");
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      setToken(null);
      delete axios.defaults.headers.common["Authorization"];
    } finally {
      setLoading(false);
    }
  };

  const login = async (identifier, password) => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.post("/auth/login/", {
        identifier: identifier,
        password: password,
      });

      const { access, refresh, user: userData } = response.data;

      localStorage.setItem("access_token", access);
      localStorage.setItem("refresh_token", refresh);
      localStorage.setItem("user", JSON.stringify(userData));

      setUser(userData);
      setToken(access);
      setError(null);

      axios.defaults.headers.common["Authorization"] = `Bearer ${access}`;

      toast.success(`Bienvenue ${userData.first_name || userData.email || userData.phone || "!"}`);

      return { success: true, user: userData };
    } catch (err) {
      console.error("Erreur de connexion:", err);
      const errorMessage = err.response?.data?.error || "Email/Téléphone ou mot de passe incorrect";
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user");
    setUser(null);
    setToken(null);
    setError(null);
    delete axios.defaults.headers.common["Authorization"];
    toast.success("Déconnexion réussie");
    window.location.href = "/login";
  };

  const value = {
    user,
    login,
    logout,
    loading,
    error,
    token,
    isAuthenticated: !!user,
    isSuperAdmin: user?.role === "superadmin",
    isAdmin: user?.role === "admin",
    isOperator: user?.role === "operator",
    isClient: user?.role === "client",
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
